import { defineStore, type StoreDefinition } from 'pinia'
import type { AddressData, AppStore, AskForm, OrderNowForm, PlaceEventSelectionOptionType, Prices } from '../types'
// sync debouncer
const debouncedRequest = useDebounceFn(
    async (sync, requestPayload) => {
        try {
            const response = await fetch(sync, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload),
                keepalive: true
            })
            return await response.json()
        } catch (_error) {}
    },
    50, // Debounce time in ms
    { rejectOnCancel: true }
)
// sync price debouncer
const priceRequest = useDebounceFn(
    async (sync, requestPayload) => {
        try {
            const response = await fetch(sync, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload),
                keepalive: true
            })
            return await response.json()
        } catch (_error) {}
    },
    50, // Debounce time in ms
    { rejectOnCancel: true }
)

const replacer = (countryName: string) => {
    countryName = countryName.replace(/\d/g, '').trim()

    const replacements: Record<string, string> = {
        'Stockholm County': 'Stockholm',
        'County Dublin': 'Dublin'
    }

    return replacements[countryName] || countryName
}

// Get place details using PlacesService (no Geocoding API needed, only Places API)
const getPlaceDetails = (placeId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const g = (window as any).google
        const service = new g.maps.places.PlacesService(document.createElement('div'))
        service.getDetails(
            { placeId, fields: ['address_components', 'formatted_address', 'geometry', 'place_id'] },
            (result: any, status: any) => {
                if (status === g.maps.places.PlacesServiceStatus.OK && result) {
                    resolve(result)
                } else {
                    reject(new Error('PlacesService failed: ' + status))
                }
            }
        )
    })
}

// Convert PlaceResult to geocode-compatible format (array with address_components)
const placeResultToGeocode = (place: any): AddressData[] => {
    return [{
        address_components: place.address_components,
        formatted_address: place.formatted_address || '',
        geometry: place.geometry,
        place_id: place.place_id || '',
        types: place.types || []
    }]
}

export const app: StoreDefinition = defineStore<'app', AppStore>('app', {
    state: (): AppStore => {
        return {
            isRessetable: true,
            activeCargo: 'pallet',

            serviceSelection: {
                floors: 0,
                driver: false,
                packing: false,
                unpacking: false,
                parking: false
            },

            from: undefined,
            to: undefined,

            afrom: undefined,
            ato: undefined,

            hadHandShakeWithApi: false,

            prices: {
                message: '',
                status: 'ok',
                companies: []
            },

            initializedMapsFrom: false,
            initializedMapsTo: false,

            main: {
                // embed version
                from: '',
                to: '',
                fromZip: '',
                toZip: '',
                fromCode: '',
                toCode: '',
                embed: false,

                // app
                autocomplete: false, // for shared data loading
                shared: {},
                initialized: false // used for shared data, data loading and initialization
            }
        }
    },

    getters: {
        countryFrom: (state) => {
            if (!state.initializedMapsFrom) {
                if (state.main.embed) {
                    return state.main.from
                }
            }

            if (state.afrom === undefined) return 'is not selected'

            const getCountry = (addressComponents: AddressData['address_components']) => {
                const countryComponent = addressComponents.find((component) => component.types.includes('country'))
                return countryComponent ? countryComponent.long_name : 'Unknown'
            }

            const country = getCountry(state.afrom[0].address_components)

            const result = country ? replacer(country) : 'is not selected'

            if (result === 'United Kingdom' || result === 'Finland' || result === 'Sweden' || result === 'Ireland') {
                // exclude to specified city
                const extractCityAndCountry = (addressComponents: AddressData['address_components']) => {
                    let cityComponent = addressComponents.find((component) =>
                        component.types.includes('administrative_area_level_2')
                    )

                    if (result === 'Ireland' || (cityComponent === undefined && result === 'Sweden')) {
                        cityComponent = addressComponents.find((component) =>
                            component.types.includes('administrative_area_level_1')
                        )
                    }

                    return cityComponent ? cityComponent.long_name : result
                }

                const getCity = (addressComponents: AddressData['address_components']) => {
                    const cityComponent = addressComponents.find((component) => component.types.includes('postal_town'))
                    return cityComponent ? cityComponent.long_name : extractCityAndCountry(addressComponents)
                }

                return replacer(getCity(state.afrom[0].address_components))
            } else {
                return result
            }
        },
        countryTo: (state) => {
            if (!state.initializedMapsTo) {
                if (state.main.embed) {
                    return state.main.to
                }
            }
            if (state.ato === undefined) return 'is not selected'

            const getCountry = (addressComponents: AddressData['address_components']) => {
                const countryComponent = addressComponents.find((component) => component.types.includes('country'))
                return countryComponent ? countryComponent.long_name : 'Unknown'
            }

            const country = getCountry(state.ato[0].address_components)

            const result = country ? replacer(country) : 'is not selected'

            if (result === 'United Kingdom' || result === 'Finland' || result === 'Sweden' || result === 'Ireland') {
                // exclude to specified city

                const extractCityAndCountry = (addressComponents: AddressData['address_components']) => {
                    let cityComponent = addressComponents.find((component) =>
                        component.types.includes('administrative_area_level_2')
                    )

                    if (result === 'Ireland' || (cityComponent === undefined && result === 'Sweden')) {
                        cityComponent = addressComponents.find((component) =>
                            component.types.includes('administrative_area_level_1')
                        )
                    }

                    return cityComponent ? cityComponent.long_name : result
                }

                const getCity = (addressComponents: AddressData['address_components']) => {
                    const cityComponent = addressComponents.find((component) => component.types.includes('postal_town'))
                    return cityComponent ? cityComponent.long_name : extractCityAndCountry(addressComponents)
                }
                return replacer(getCity(state.ato[0].address_components))
            } else {
                return result
            }
        },
        fromZip: (state) => {
            if (!state.initializedMapsFrom) {
                if (state.main.embed) {
                    return state.main.fromZip
                }
            }

            if (state.afrom === undefined) return 'is not selected'

            const getCountryZipCode = (addressComponents: AddressData['address_components']) => {
                const countryComponent = addressComponents.find((component) => component.types.includes('postal_code'))
                return countryComponent ? countryComponent.short_name : 'Unknown'
            }

            const countryZipCode = getCountryZipCode(state.afrom[0].address_components)

            return countryZipCode ? countryZipCode : 'is not selected'
        },
        toZip: (state) => {
            if (!state.initializedMapsTo) {
                if (state.main.embed) {
                    return state.main.toZip
                }
            }

            if (state.ato === undefined) return 'is not selected'

            const getCountryZipCode = (addressComponents: AddressData['address_components']) => {
                const countryComponent = addressComponents.find((component) => component.types.includes('postal_code'))
                return countryComponent ? countryComponent.short_name : 'Unknown'
            }

            const countryZipCode = getCountryZipCode(state.ato[0].address_components)

            return countryZipCode ? countryZipCode : 'is not selected'
        },
        flag: (state) => {
            const from = (address: AddressData['address_components']) => {
                return address.find((c) => c.types.includes('country'))?.short_name.toLowerCase() || null
            }
            return {
                from: state.afrom ? from(state.afrom[0]?.address_components) : state.main.fromCode.toLowerCase(),
                to: state.ato ? from(state.ato[0]?.address_components) : state.main.toCode.toLowerCase()
            }
        }
    },

    actions: {
        async setFrom(place: PlaceEventSelectionOptionType) {
            // Set from synchronously first so the display updates immediately on blur
            this.from = place

            const placeResult = await getPlaceDetails(place.place_id)
            this.afrom = placeResultToGeocode(placeResult)

            this.initializedMapsFrom = true

            this.cargoPrice?.()
        },
        async setTo(place: PlaceEventSelectionOptionType) {
            // Set to synchronously first so the display updates immediately on blur
            this.to = place

            const placeResult = await getPlaceDetails(place.place_id)
            this.ato = placeResultToGeocode(placeResult)

            this.initializedMapsTo = true

            this.cargoPrice?.()
        },
        cargoPrice() {
            if (
                this.countryTo === 'is not selected' ||
                this.countryFrom === 'is not selected' ||
                !this.from?.place_id ||
                !this.to?.place_id
            ) {
                return
            }

            priceRequest(import.meta.env.VITE_BACKEND + '/cargo', {
                from: this.countryFrom,
                to: this.countryTo,
                fromZip: this.fromZip,
                toZip: this.toZip,
                fromPlaceId: this.from?.place_id,
                toPlaceId: this.to?.place_id,
                activeCargo: this.activeCargo
            }).then((data: Prices) => {
                this.hadHandShakeWithApi = true
                if (data !== undefined) {
                    this.prices = data

                    // track
                    if (zaraz) {
                        zaraz?.track('Calculate', {
                            event_category: 'Calculator',
                            event_action: 'Calculate',
                            event_label: 'How many times a full calculation was performed.'
                        })
                    }
                } else {
                    this.prices = {
                        companies: [],
                        status: 'error',
                        message: 'Failed to get prices data'
                    }
                }
            })
        },
        // forms
        onShare(cargo: any): void {
            debouncedRequest(import.meta.env.VITE_BACKEND + '/shareCargo', cargo).then((r) => console.log(r))
        },
        shareForm(data: { email: string; url: string }): void {
            debouncedRequest(import.meta.env.VITE_BACKEND + '/shareToEmail', data).then((r) => console.log(r))
        },
        askForm(data: AskForm): void {
            debouncedRequest(import.meta.env.VITE_BACKEND + '/ask', data).then((r) => console.log(r))
        },
        orderForm(data: OrderNowForm): void {
            debouncedRequest(import.meta.env.VITE_BACKEND + '/order', data).then((r) => console.log(r))
        },
        loadSharedData(cargo: string): void {
            if (cargo === '') return

            debouncedRequest(import.meta.env.VITE_BACKEND + '/getCargo', { cargo }).then((r: Record<string, any>) => {
                if (r !== undefined) {
                    this.main = {
                        from: '',
                        to: '',
                        fromZip: '',
                        toZip: '',
                        fromCode: '',
                        toCode: '',
                        embed: false,
                        initialized: true,
                        autocomplete: true,
                        shared: r
                    }
                }
            })
        }
    }
})

export type appT = ReturnType<typeof app>
